import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Step,
  StepButton,
  Stepper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublishIcon from '@mui/icons-material/Publish';
import SaveIcon from '@mui/icons-material/Save';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { AddCapabilityDialog } from '../components/serviceOnboarding/AddCapabilityDialog';
import { AdapterConfigLabelField } from '../components/serviceOnboarding/AdapterConfigLabelField';
import { AdapterServiceKeyField } from '../components/serviceOnboarding/AdapterServiceKeyField';
import { CanonicalFieldTable } from '../components/serviceOnboarding/CanonicalFieldTable';
import {
  AdapterConfigContractPicker,
  type AdapterContractApplyPayload,
} from '../components/serviceOnboarding/AdapterConfigContractPicker';
import type { AdapterCapabilityDto, CapabilityTemplateDto } from '../domain/adapterService';
import type { CanonicalFieldDef } from '../domain/canonicalFieldDef';
import { messageFromAdminConsoleAxiosError } from '../lib/axiosErrorMessage';
import {
  commitExampleJsonToDraftFields,
  leafPathsFromExampleJson,
  mergeExampleJsonFromPaths,
  mergeFieldsFromPaths,
  stringifyExampleJson,
  formatExampleJsonText,
} from '../lib/canonicalFieldJsonSync';
import {
  useAdapterService,
  useDeleteAdapterCapability,
  usePatchAdapterService,
  usePublishAdapterService,
  usePublishPreview,
  useSaveAdapterCapability,
} from '../hooks/useAdapterServices';

const WIZARD_STEPS = ['Service', 'Capabilities', 'Request & response', 'Review'];

function emptyCapability(
  serviceId: string,
  sortOrder: number,
): Partial<AdapterCapabilityDto> & { capability: string; label: string } {
  return {
    serviceId,
    capability: '',
    label: '',
    stepKey: null,
    sortOrder,
    httpMethod: 'POST',
    pathTemplate: '',
    canonicalRequest: {},
    canonicalResponse: {},
    canonicalRequestFields: [],
    canonicalResponseFields: [],
    stepDefaults: {},
  };
}

function suggestCapabilityId(label: string, prefix = 'CUSTOM'): string {
  const slug = label
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return slug ? `${prefix}.${slug}` : `${prefix}.CAPABILITY`;
}

function CapabilityTabs({
  capabilities,
  capTab,
  onChange,
  draftingNew,
  draftLabel,
  draftCapability,
}: {
  capabilities: AdapterCapabilityDto[];
  capTab: number;
  onChange: (index: number) => void;
  draftingNew?: boolean;
  draftLabel?: string;
  draftCapability?: string;
}) {
  if (capabilities.length === 0 && !draftingNew) return null;
  return (
    <Tabs value={capTab} onChange={(_, v) => onChange(v)} variant="scrollable" sx={{ mb: 1 }}>
      {capabilities.map(c => (
        <Tab
          key={c.id}
          label={
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" component="span" display="block">
                {c.label || 'Untitled'}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="span" display="block">
                {c.capability}
              </Typography>
            </Box>
          }
        />
      ))}
      {draftingNew ? (
        <Tab
          key="__new_capability__"
          label={
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" component="span" display="block">
                {draftLabel?.trim() || 'New capability'} (unsaved)
              </Typography>
              <Typography variant="caption" color="text.secondary" component="span" display="block">
                {draftCapability?.trim() || '—'}
              </Typography>
            </Box>
          }
        />
      ) : null}
    </Tabs>
  );
}

export function ServiceOnboardingEditorPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: service, isLoading, refetch } = useAdapterService(serviceId);
  const publish = usePublishAdapterService();
  const saveCap = useSaveAdapterCapability(serviceId ?? '');
  const patchService = usePatchAdapterService(serviceId ?? '');
  const deleteCap = useDeleteAdapterCapability(serviceId ?? '');

  const [activeStep, setActiveStep] = useState(0);
  const [capTab, setCapTab] = useState(0);
  const [draft, setDraft] = useState<Partial<AdapterCapabilityDto> & { capability: string; label: string } | null>(
    null,
  );
  const [publishMsg, setPublishMsg] = useState<string | null>(null);
  const [serviceDisplayName, setServiceDisplayName] = useState('');
  const [serviceConfigLabel, setServiceConfigLabel] = useState('default');
  const [serviceAdapterKey, setServiceAdapterKey] = useState('');
  const [keyChangeWarning, setKeyChangeWarning] = useState<string | null>(null);
  const [serviceSaveMsg, setServiceSaveMsg] = useState<string | null>(null);
  const [addCapOpen, setAddCapOpen] = useState(false);
  const [isDraftingNewCapability, setIsDraftingNewCapability] = useState(false);
  const [requestExampleJsonText, setRequestExampleJsonText] = useState('{}');
  const [responseExampleJsonText, setResponseExampleJsonText] = useState('{}');
  const [jsonEditorError, setJsonEditorError] = useState<string | null>(null);
  const jsonTextDraftKeyRef = useRef<string | null>(null);

  const capabilities = useMemo(() => service?.capabilities ?? [], [service?.capabilities]);
  const publishPreview = usePublishPreview(serviceId, activeStep === 3);

  useEffect(() => {
    if (service) {
      setServiceDisplayName(service.displayName);
      setServiceConfigLabel(service.configLabel ?? 'default');
      setServiceAdapterKey(service.adapterServiceKey);
    }
  }, [service?.displayName, service?.configLabel, service?.adapterServiceKey, service?.id]);

  useEffect(() => {
    if (!serviceId) return;
    if (!capabilities.length) {
      if (!isDraftingNewCapability) {
        setDraft(emptyCapability(serviceId, 0));
        setCapTab(0);
      }
      return;
    }
    if (isDraftingNewCapability || capTab >= capabilities.length) {
      return;
    }
    setDraft({ ...capabilities[capTab] });
  }, [serviceId, capabilities, capTab, isDraftingNewCapability]);

  const draftJsonKey = draft ? `${draft.id ?? 'new'}:${draft.capability}:${activeStep}` : null;

  useEffect(() => {
    if (!draft || activeStep !== 2) return;
    if (jsonTextDraftKeyRef.current === draftJsonKey) return;
    jsonTextDraftKeyRef.current = draftJsonKey;
    setRequestExampleJsonText(stringifyExampleJson(draft.canonicalRequest));
    setResponseExampleJsonText(stringifyExampleJson(draft.canonicalResponse));
    setJsonEditorError(null);
  }, [draft, draftJsonKey, activeStep]);

  const loadCapAt = useCallback(
    (index: number) => {
      setCapTab(index);
      if (index >= capabilities.length) {
        return;
      }
      setIsDraftingNewCapability(false);
      const cap = capabilities[index];
      if (cap) {
        setDraft({ ...cap });
      }
    },
    [capabilities],
  );

  const capabilityTabsProps = {
    capabilities,
    capTab,
    onChange: loadCapAt,
    draftingNew: isDraftingNewCapability,
    draftLabel: draft?.label,
    draftCapability: draft?.capability,
  };

  const buildDraftWithCommittedJson = useCallback(() => {
    if (!draft) return null;
    const requestCommit = commitExampleJsonToDraftFields(
      requestExampleJsonText,
      draft.canonicalRequestFields ?? [],
    );
    if (!requestCommit.ok) {
      setJsonEditorError(`Request JSON: ${requestCommit.error}`);
      return null;
    }
    const responseCommit = commitExampleJsonToDraftFields(
      responseExampleJsonText,
      draft.canonicalResponseFields ?? [],
    );
    if (!responseCommit.ok) {
      setJsonEditorError(`Response JSON: ${responseCommit.error}`);
      return null;
    }
    setJsonEditorError(null);
    return {
      ...draft,
      canonicalRequest: requestCommit.object,
      canonicalRequestFields: requestCommit.fields,
      canonicalResponse: responseCommit.object,
      canonicalResponseFields: responseCommit.fields,
    };
  }, [draft, requestExampleJsonText, responseExampleJsonText]);

  const handleSaveCapability = async () => {
    const committed = buildDraftWithCommittedJson();
    if (!committed || !serviceId) return;
    setDraft(committed);
    const savedSortOrder = committed.sortOrder ?? capabilities.length;
    await saveCap.mutateAsync({
      id: committed.id,
      capability: committed.capability.trim(),
      label: committed.label.trim(),
      stepKey: committed.stepKey ?? null,
      sortOrder: savedSortOrder,
      httpMethod: committed.httpMethod ?? 'POST',
      pathTemplate: committed.pathTemplate ?? '',
      canonicalRequest: committed.canonicalRequest ?? {},
      canonicalResponse: committed.canonicalResponse ?? {},
      canonicalRequestFields: committed.canonicalRequestFields ?? [],
      canonicalResponseFields: committed.canonicalResponseFields ?? [],
      stepDefaults: committed.stepDefaults ?? {},
    });
    setIsDraftingNewCapability(false);
    setCapTab(savedSortOrder);
    await refetch();
  };

  const applyTemplateToDraft = (template: CapabilityTemplateDto | null) => {
    if (!serviceId) return;
    if (!template) {
      const sortOrder = capabilities.length;
      const label = `Capability ${sortOrder + 1}`;
      setDraft({
        ...emptyCapability(serviceId, sortOrder),
        label,
        capability: suggestCapabilityId(label, service?.adapterServiceKey?.toUpperCase().replace(/-/g, '_') ?? 'CUSTOM'),
      });
      setCapTab(capabilities.length);
      setIsDraftingNewCapability(true);
      return;
    }
    const suffix = template.source === 'existing' ? '_COPY' : '';
    setDraft({
      serviceId,
      capability: template.capability + suffix,
      label: template.label,
      stepKey: template.stepKey,
      sortOrder: capabilities.length,
      httpMethod: template.httpMethod,
      pathTemplate: template.pathTemplate,
      canonicalRequest: { ...template.canonicalRequest },
      canonicalResponse: { ...template.canonicalResponse },
      canonicalRequestFields: [...template.canonicalRequestFields],
      canonicalResponseFields: [...template.canonicalResponseFields],
      stepDefaults: { ...template.stepDefaults },
    });
    setCapTab(capabilities.length);
    setIsDraftingNewCapability(true);
  };

  const handleAdapterContractApply = (payload: AdapterContractApplyPayload) => {
    if (!draft) return;
    setDraft({
      ...draft,
      canonicalRequest: payload.canonicalRequest,
      canonicalResponse: payload.canonicalResponse,
      canonicalRequestFields: payload.canonicalRequestFields,
      canonicalResponseFields: payload.canonicalResponseFields,
    });
    if (activeStep === 2) {
      setRequestExampleJsonText(stringifyExampleJson(payload.canonicalRequest));
      setResponseExampleJsonText(stringifyExampleJson(payload.canonicalResponse));
      jsonTextDraftKeyRef.current = `${draft.id ?? 'new'}:${draft.capability}:${activeStep}`;
    }
  };

  const handleRequestFieldsChange = (canonicalRequestFields: CanonicalFieldDef[]) => {
    if (!draft) return;
    const canonicalRequest = mergeExampleJsonFromPaths(
      canonicalRequestFields.map(field => field.path),
      draft.canonicalRequest ?? {},
    );
    setDraft({
      ...draft,
      canonicalRequestFields,
      canonicalRequest,
    });
    if (activeStep === 2) {
      setRequestExampleJsonText(stringifyExampleJson(canonicalRequest));
    }
  };

  const handleRequestJsonChange = (value: string | undefined) => {
    setRequestExampleJsonText(value ?? '');
    setJsonEditorError(null);
  };

  const handleResponseFieldsChange = (canonicalResponseFields: CanonicalFieldDef[]) => {
    if (!draft) return;
    const canonicalResponse = mergeExampleJsonFromPaths(
      canonicalResponseFields.map(field => field.path),
      draft.canonicalResponse ?? {},
    );
    setDraft({
      ...draft,
      canonicalResponseFields,
      canonicalResponse,
    });
    if (activeStep === 2) {
      setResponseExampleJsonText(stringifyExampleJson(canonicalResponse));
    }
  };

  const handleResponseJsonChange = (value: string | undefined) => {
    setResponseExampleJsonText(value ?? '');
    setJsonEditorError(null);
  };

  const handleFormatRequestJson = () => {
    const result = formatExampleJsonText(requestExampleJsonText);
    if (!result.ok) {
      setJsonEditorError(`Request JSON: ${result.error}`);
      return;
    }
    setJsonEditorError(null);
    setRequestExampleJsonText(result.formatted);
    if (!draft) return;
    setDraft({
      ...draft,
      canonicalRequest: result.parsed,
      canonicalRequestFields: mergeFieldsFromPaths(
        leafPathsFromExampleJson(result.parsed),
        draft.canonicalRequestFields ?? [],
      ),
    });
  };

  const handleFormatResponseJson = () => {
    const result = formatExampleJsonText(responseExampleJsonText);
    if (!result.ok) {
      setJsonEditorError(`Response JSON: ${result.error}`);
      return;
    }
    setJsonEditorError(null);
    setResponseExampleJsonText(result.formatted);
    if (!draft) return;
    setDraft({
      ...draft,
      canonicalResponse: result.parsed,
      canonicalResponseFields: mergeFieldsFromPaths(
        leafPathsFromExampleJson(result.parsed),
        draft.canonicalResponseFields ?? [],
      ),
    });
  };

  const handleGoToReview = () => {
    const committed = buildDraftWithCommittedJson();
    if (!committed) return;
    setDraft(committed);
    setActiveStep(3);
  };

  const handleDeleteCapability = async () => {
    if (!draft?.id || !serviceId) return;
    if (!window.confirm(`Delete capability "${draft.label}" (${draft.capability})?`)) return;
    await deleteCap.mutateAsync(draft.id);
    setIsDraftingNewCapability(false);
    setCapTab(0);
    await refetch();
  };

  const handlePatchService = async () => {
    if (!serviceId) return;
    setServiceSaveMsg(null);
    try {
      const result = await patchService.mutateAsync({
        displayName: serviceDisplayName.trim(),
        adapterServiceKey: serviceAdapterKey.trim(),
        configLabel: serviceConfigLabel.trim() || 'default',
      });
      setKeyChangeWarning(result.keyChangeWarning ?? null);
      const renamed = result.renamedReferences;
      setServiceSaveMsg(
        renamed
          ? `Service saved. Updated ${renamed.endpointMappingsUpdated} endpoint(s), ${renamed.routingRowsUpdated} routing row(s), ${renamed.vendorPayloadsUpdated} vendor payload(s).`
          : 'Service saved.',
      );
      await refetch();
    } catch {
      setServiceSaveMsg(null);
    }
  };

  const handlePublish = async () => {
    if (!serviceId) return;
    setPublishMsg(null);
    const result = await publish.mutateAsync(serviceId);
    if (result.ok) {
      setPublishMsg(`Published ${result.rowCount} config row(s) at ${result.syncedAt}`);
      await refetch();
    } else {
      setPublishMsg(result.error ?? 'Publish failed');
    }
  };

  if (isLoading || !service) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button component={RouterLink} to="/service-onboarding" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
        Back to services
      </Button>

      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {service.displayName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <code>{service.adapterServiceKey}</code> · label <code>{service.configLabel}</code> · {service.flowType}
      </Typography>

      <Stepper activeStep={activeStep} nonLinear sx={{ mb: 3 }}>
        {WIZARD_STEPS.map((label, index) => (
          <Step key={label} completed={activeStep > index}>
            <StepButton color="inherit" onClick={() => setActiveStep(index)}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Stack spacing={2} sx={{ maxWidth: 560 }}>
          <TextField
            label="Display name"
            value={serviceDisplayName}
            onChange={e => setServiceDisplayName(e.target.value)}
            fullWidth
          />
          <AdapterServiceKeyField value={serviceAdapterKey} onChange={setServiceAdapterKey} />
          {keyChangeWarning && (
            <Alert severity="warning">{keyChangeWarning}</Alert>
          )}
          {patchService.isError && (
            <Alert severity="error">{messageFromAdminConsoleAxiosError(patchService.error)}</Alert>
          )}
          {serviceSaveMsg && !patchService.isError && (
            <Alert severity="success">{serviceSaveMsg}</Alert>
          )}
          <AdapterConfigLabelField selectOnly value={serviceConfigLabel} onChange={setServiceConfigLabel} />
          <Chip label={`Status: ${service.status}`} color={service.status === 'published' ? 'success' : 'default'} />
          {service.lastPublishedAt && (
            <Typography variant="caption" color="text.secondary">
              Last published {new Date(service.lastPublishedAt).toLocaleString()}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            disabled={patchService.isPending}
            onClick={() => void handlePatchService()}
          >
            Save service
          </Button>
          <Button variant="contained" onClick={() => setActiveStep(1)}>
            Next: Capabilities
          </Button>
        </Stack>
      )}

      {activeStep === 1 && draft && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddCapOpen(true)}>
              Add capability
            </Button>
            {draft.id && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => void handleDeleteCapability()}
              >
                Delete capability
              </Button>
            )}
          </Stack>
          <AddCapabilityDialog
            open={addCapOpen}
            serviceId={serviceId ?? ''}
            onClose={() => setAddCapOpen(false)}
            onApply={t => applyTemplateToDraft(t)}
          />
          <Alert severity="info">
            <strong>Capability ID</strong> (e.g. <code>INCOME.GET_TX_AUTHORIZE_LOGIN</code>) is the runtime id for{' '}
            <code>executeService</code> and vendor config keys. <strong>Display name</strong> is admin UI only.
            Request/response field catalogs drive Provider Onboarding <code>p.*</code> and response mappings.
          </Alert>
          <CapabilityTabs {...capabilityTabsProps} />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Capability ID"
              value={draft.capability}
              onChange={e => setDraft({ ...draft, capability: e.target.value })}
              fullWidth
              helperText="Used by adapter runtime and Provider Onboarding. Must be unique within this service."
            />
            <TextField
              label="Display name"
              value={draft.label}
              onChange={e => setDraft({ ...draft, label: e.target.value })}
              fullWidth
              helperText="UI/catalog label only — not written to adapter_configs."
            />
          </Stack>
          <TextField
            label="Step key"
            value={draft.stepKey ?? ''}
            onChange={e => setDraft({ ...draft, stepKey: e.target.value || null })}
            required={capabilities.length > 1}
            helperText={
              capabilities.length > 1
                ? 'Required when multiple capabilities exist (must be unique).'
                : 'Optional for single-capability services. Required for multi-step adapter flows.'
            }
          />
          <Alert severity="info" sx={{ mt: 1 }}>
            HTTP method, path, timeouts, and retries are configured per vendor in Provider Onboarding — not here.
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => void handleSaveCapability()}
              disabled={saveCap.isPending || !draft.capability.trim()}
            >
              Save capability
            </Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button variant="contained" onClick={() => setActiveStep(2)}>
              Next: Request & response
            </Button>
          </Stack>
        </Stack>
      )}

      {activeStep === 2 && draft && (
        <Stack spacing={2}>
          <CapabilityTabs {...capabilityTabsProps} />
          <Alert severity="info">
            Catalog <strong>request</strong> fields as <code>p.*</code> paths (values your service passes to{' '}
            <code>executeService</code>) and <strong>response</strong> fields as normalized keys in{' '}
            <code>result.data</code> — not vendor wire names or this service’s HTTP DTOs. Provider Onboarding
            maps vendor bodies to these paths.
          </Alert>
          <AdapterConfigContractPicker
            serviceId={serviceId ?? ''}
            adapterServiceKey={service.adapterServiceKey}
            defaultConfigLabel={service.configLabel ?? 'default'}
            capability={draft.capability}
            onApply={handleAdapterContractApply}
          />
          <CanonicalFieldTable
            title="Request fields (adapter payload p)"
            fields={draft.canonicalRequestFields ?? []}
            onChange={handleRequestFieldsChange}
          />
          <CanonicalFieldTable
            title="Response fields (normalized)"
            fields={draft.canonicalResponseFields ?? []}
            onChange={handleResponseFieldsChange}
          />
          <Typography variant="subtitle2">Example request JSON</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" variant="outlined" onClick={handleFormatRequestJson}>
              Format JSON
            </Button>
          </Stack>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', height: 200 }}>
            <Editor
              height="200px"
              language="json"
              value={requestExampleJsonText}
              onChange={handleRequestJsonChange}
              options={{ minimap: { enabled: false }, wordWrap: 'on' }}
            />
          </Box>
          <Typography variant="subtitle2">Example response JSON</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button size="small" variant="outlined" onClick={handleFormatResponseJson}>
              Format JSON
            </Button>
          </Stack>
          {jsonEditorError && (
            <Alert severity="error">{jsonEditorError}</Alert>
          )}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', height: 200 }}>
            <Editor
              height="200px"
              language="json"
              value={responseExampleJsonText}
              onChange={handleResponseJsonChange}
              options={{ minimap: { enabled: false }, wordWrap: 'on' }}
            />
          </Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setActiveStep(1)}>Back</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={() => void handleSaveCapability()}>
              Save
            </Button>
            <Button variant="contained" onClick={handleGoToReview}>
              Review
            </Button>
          </Stack>
        </Stack>
      )}

      {activeStep === 3 && (
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Service summary
              </Typography>
              <Typography variant="body2">
                Key: <code>{service.adapterServiceKey}</code>
              </Typography>
              <Typography variant="body2">
                Adapter config label: <code>{serviceConfigLabel}</code>
              </Typography>
              <Typography variant="body2">Flow: {service.flowType}</Typography>
              <Typography variant="body2">Status: {service.status}</Typography>
              {service.lastPublishedAt && (
                <Typography variant="body2">
                  Last published: {new Date(service.lastPublishedAt).toLocaleString()}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Display name</TableCell>
                <TableCell>Capability ID</TableCell>
                <TableCell>Step</TableCell>
                <TableCell>Request fields</TableCell>
                <TableCell>Response fields</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {capabilities.map(c => (
                <TableRow key={c.id}>
                  <TableCell>{c.label}</TableCell>
                  <TableCell>
                    <code>{c.capability}</code>
                  </TableCell>
                  <TableCell>{c.stepKey ?? '—'}</TableCell>
                  <TableCell>{(c.canonicalRequestFields ?? []).length}</TableCell>
                  <TableCell>{(c.canonicalResponseFields ?? []).length}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {serviceConfigLabel !== (service.configLabel ?? 'default') && (
            <Alert severity="warning">
              Adapter config label was changed but not saved. Save the service on step 0 for an accurate publish
              preview.
            </Alert>
          )}
          {publishPreview.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {publishPreview.data && (
            <Alert severity={publishPreview.data.adapterConfigConfigured ? 'info' : 'warning'}>
              {publishPreview.data.adapterConfigConfigured ? (
                <>
                  Catalog publish marks this service <strong>published</strong> and bumps{' '}
                  <code>Adapters:Sentinel</code> for label <code>{publishPreview.data.configLabel}</code>. It does{' '}
                  <strong>not</strong> write <code>config:service:*</code> provider rows — Provider Onboarding sync does
                  that when you onboard vendors.
                </>
              ) : (
                <>
                  Adapter config database is not configured — publish will fail until{' '}
                  <code>ADAPTER_CONFIG_DATABASE_URL</code> is set.
                </>
              )}
            </Alert>
          )}

          <Alert severity="warning">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Publish checklist
            </Typography>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              <li>Request/response field catalogs are stored in admin DB and exposed via GET /adapter-services/catalog</li>
              <li>Provider Onboarding uses those targets for vendor request/response mapping</li>
              <li>Runtime adapter config (URLs, paths, JSONata) is written when vendors sync — not on service publish</li>
              <li>Review catalogs against backend executeService call sites before publishing</li>
            </ul>
          </Alert>

          {publishMsg && (
            <Alert severity={publishMsg.startsWith('Published') ? 'success' : 'error'}>{publishMsg}</Alert>
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setActiveStep(2)}>Back</Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PublishIcon />}
              disabled={publish.isPending || capabilities.length === 0}
              onClick={() => void handlePublish()}
            >
              {publish.isPending ? 'Publishing…' : 'Publish catalog'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
